import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useSettings } from "../../lib/useSettings";
import InstallPWAButton from "../../components/admin/InstallPWAButton";
import CockpitWhatsNewModal from "../../components/admin/CockpitWhatsNewModal";
import {
  LayoutDashboard, Wrench, Image as ImageIcon, FileText, HelpCircle, MapPin, Users,
  BarChart3, Search, Settings, LogOut, TrendingUp, Globe, Sparkles,
  FolderOpen, Link2, Palette, Package, Store, ShoppingBag, Layout as LayoutIcon, QrCode, Calendar, Shield, Menu, X, PhoneCall, Heart as HeartIcon, CreditCard, TestTube2,
  GitCommitHorizontal, Layout, Building2, Wand2, Receipt, Mail, Truck, Briefcase, CalendarRange, Bell, ShieldCheck, Award, BadgeEuro, FileCheck2, Rocket,
} from "lucide-react";

/**
 * Sidebar structure — builds dynamically from settings so widgets shown depend on what's enabled.
 * Each section has a distinctive color to ease navigation.
 */
function buildSections(s, user) {
  const shopOn = !!s?.shop_enabled;
  const clickCollectOn = shopOn && !!s?.shop_click_collect_enabled;
  const bookingOn = !!s?.booking_enabled;
  const rentalOn = !!s?.rental_enabled;
  const isHub = !!s?.is_hub;
  const wishlistOn = shopOn && !!s?.wishlist_enabled;
  const popupOn = !!s?.popup_callback_enabled;
  const rgpdOn = !!s?.rgpd_generator_enabled;

  const sections = [
    {
      label: "Pilotage",
      color: "#60A5FA",
      links: [
        { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/admin/widgets", label: "Console widgets", icon: Sparkles },
        { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
      ],
    },
    {
      label: "Relations clients",
      color: "#F472B6",   // pink — CRM
      links: [
        { to: "/admin/leads", label: "Demandes de devis", icon: Users, badgeKey: "leads" },
        popupOn && { to: "/admin/leads?type=rappel", label: "Demandes de rappel", icon: PhoneCall, badgeKey: "callbacks" },
        shopOn && { to: "/admin/orders", label: "Commandes", icon: ShoppingBag },
        bookingOn && { to: "/admin/bookings", label: "Réservations", icon: Calendar },
        rentalOn && { to: "/admin/rentals", label: "Locations", icon: CalendarRange, badgeKey: "rentals" },
      ].filter(Boolean),
    },
    {
      label: "Offres & catalogue",
      color: "#FB923C",   // orange — catalogue
      links: [
        { to: "/admin/services", label: "Prestations", icon: Wrench },
        shopOn && { to: "/admin/products", label: "Produits", icon: Package },
        clickCollectOn && { to: "/admin/pickup", label: "Click & Collect", icon: Store },
        wishlistOn && { to: "/admin/wishlist", label: "Favoris produits", icon: HeartIcon },
        bookingOn && { to: "/admin/bookings-config", label: "Config réservation", icon: Settings },
        rentalOn && { to: "/admin/rentals-calendar", label: "Planning location", icon: CalendarRange },
        rentalOn && { to: "/admin/rentals-dashboard", label: "Tableau location", icon: BarChart3 },
      ].filter(Boolean),
    },
    {
      label: "Contenus du site",
      color: "#A78BFA",   // violet — editorial
      links: [
        { to: "/admin/sections", label: "Sections page d'accueil", icon: Layout },
        { to: "/admin/realisations", label: "Réalisations", icon: ImageIcon },
        { to: "/admin/blog", label: "Blog", icon: FileText },
        { to: "/admin/team", label: "Équipe", icon: Users },
        { to: "/admin/faq", label: "FAQ", icon: HelpCircle },
        { to: "/admin/media", label: "Médiathèque", icon: FolderOpen },
      ],
    },
    {
      label: "SEO & Acquisition",
      color: "#34D399",   // emerald
      links: [
        { to: "/admin/zones", label: "Zones", icon: MapPin },
        { to: "/admin/rank-tracker", label: "Rank Tracker", icon: TrendingUp },
        { to: "/admin/local-seo", label: "Local SEO", icon: Sparkles },
        { to: "/admin/short-links", label: "Liens trackés", icon: Link2 },
      ],
    },
    popupOn && {
      label: "Marketing",
      color: "#22D3EE",   // cyan
      links: [
        { to: "/admin/popup-callback", label: "Popup rétention", icon: PhoneCall },
      ].filter(Boolean),
    },
    {
      label: "Configuration",
      color: "#94A3B8",   // slate
      links: [
        { to: "/admin/header", label: "Header & Menu", icon: LayoutIcon },
        { to: "/admin/themes", label: "Thèmes", icon: Palette },
        { to: "/admin/payments", label: "Paiements (Stripe)", icon: CreditCard },
        shopOn && { to: "/admin/billing", label: "Facturation & TVA", icon: Receipt },
        shopOn && { to: "/admin/shipping", label: "Livraison & Food", icon: Truck },
        shopOn && { to: "/admin/rules/approval", label: "Approbation commandes", icon: ShieldCheck },
        shopOn && { to: "/admin/rules/b2b", label: "Comptes PRO · B2B", icon: Briefcase },
        shopOn && { to: "/admin/rules/loyalty", label: "Fidélité & Wallet", icon: Award },
        { to: "/admin/emails", label: "Emails & SMTP", icon: Mail },
        rgpdOn && { to: "/admin/legal", label: "RGPD & mentions", icon: Shield },
        { to: "/admin/legal-docs", label: "CGV & Livraison", icon: FileText },
        { to: "/admin/settings", label: "Paramètres", icon: Settings },
      ].filter(Boolean),
    },
    {
      label: "Technique",
      color: "#FCD34D",   // amber
      agencyOnly: true,
      links: [
        { to: "/admin/technical", label: "Sauvegardes & outils", icon: Shield, agencyOnly: true },
      ].filter(Boolean),
    },
  ].filter(Boolean);

  return sections.filter(sec => !sec.agencyOnly || user?.role === "admin")
    .map(sec => ({ ...sec, links: sec.links.filter(l => !l.agencyOnly || user?.role === "admin") }))
    .filter(sec => sec.links.length > 0);
}


export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { settings: s } = useSettings();
  const nav = useNavigate();
  const loc = useLocation();
  const [badges, setBadges] = useState({ leads: 0, cockpit: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  const [agencyOpen, setAgencyOpen] = useState(false);
  const [cockpit, setCockpit] = useState(null);
  const [agency, setAgency] = useState(null);     // { paired, profile, error }

  // Auto-close sidebar on route change (mobile UX)
  useEffect(() => { setSidebarOpen(false); }, [loc.pathname]);

  // Load onboarding status (widget jauge)
  useEffect(() => {
    if (!user) return;
    api.get("/admin/onboarding/status")
      .then(r => setOnboarding(r.data))
      .catch(() => {});
  }, [user, loc.pathname]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const [dashR, cockpitR, agencyR] = await Promise.all([
          api.get("/dashboard", { params: { days: 30 } }),
          api.get("/cockpit/meta").catch(() => ({ data: null })),
          api.get("/agency/remote-profile").catch(() => ({ data: { paired: false } })),
        ]);
        const unread = cockpitR.data?.unread_count || 0;
        setBadges({ leads: dashR.data.leads_new || 0, cockpit: unread, callbacks: dashR.data.callbacks_new || 0 });
        setCockpit(cockpitR.data);
        setAgency(agencyR.data);
      } catch {}
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [user, loc.pathname]);

  if (!user) return <Navigate to="/admin/login" replace />;

  // Sprint E++ — Sur le déploiement Accès Agence (is_hub=true), on redirige vers /agency
  // SAUF si l'admin a explicitement demandé le back-office (via clic "Back-office de ce site"
  // depuis /agency, qui pose le flag sessionStorage 'force_admin').
  if (s?.is_hub === true && user.role === "admin" && !sessionStorage.getItem("force_admin")) {
    return <Navigate to="/agency/dashboard" replace />;
  }

  const company = s?.company_short_name || s?.company_name || "Entreprise";
  const m = company.match(/^(.*?)(AIR)(.*)$/i);
  const sections = buildSections(s, user);

  return (
    <div className="admin-scope min-h-screen flex bg-[hsl(var(--background))]">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Fermer le menu"
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          data-testid="admin-sidebar-backdrop"
        />
      )}

      <aside
        className={`bg-[color:var(--brand-navy)] text-white flex-shrink-0 flex flex-col overflow-hidden transition-transform duration-300
          fixed lg:sticky top-0 left-0 z-50 h-screen w-[280px] lg:w-64
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="admin-sidebar"
      >
        {/* Brand + close mobile */}
        <div className="p-4 border-b border-white/10 flex-shrink-0 flex items-start justify-between gap-2">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            {s?.logo_url ? (
              <img src={s.logo_url} alt="" className="w-9 h-9 rounded-full bg-white p-0.5 object-contain shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-white/10 border border-dashed border-white/30 flex items-center justify-center shrink-0">
                <span className="text-[8px] font-black text-white/60">LOGO</span>
              </div>
            )}
            <div className="leading-tight min-w-0 flex-1">
              <div className="font-display font-extrabold text-sm tracking-tight truncate">
                {m ? <>{m[1]}<span className="text-[color:var(--brand-sky)]">{m[2]}</span>{m[3]}</> : company}
              </div>
              <Link to="/admin/cockpit" className="text-[9px] text-white/40 uppercase tracking-[0.14em] hover:text-[color:var(--brand-sky)] transition-colors block truncate" data-testid="sidebar-cockpit-version">
                {cockpit?.product_name || "COCKPIT"}
              </Link>
              <Link to="/admin/cockpit" className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-white/5 hover:bg-[color:var(--brand-sky)]/20 transition-colors text-[9px] font-mono text-white/70 hover:text-[color:var(--brand-sky)]">
                <span className="w-1 h-1 rounded-full bg-emerald-400" />
                v{cockpit?.version || "1.1.1"}
              </Link>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/70"
            aria-label="Fermer"
            data-testid="admin-sidebar-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="p-3 flex-1 overflow-y-auto">
          {sections.filter(section => !section.agencyOnly || user?.role === "admin").map((section) => (
            <div key={section.label} className="mb-4">
              <div className="px-3 mb-1.5 flex items-center gap-1.5 font-bold">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: section.color, boxShadow: `0 0 6px ${section.color}` }} />
                <span className="text-[9px] uppercase tracking-[0.22em]" style={{ color: section.color }}>{section.label}</span>
                {section.agencyOnly && <Shield className="w-2.5 h-2.5 ml-1" style={{ color: section.color }} />}
              </div>
              <div className="space-y-0.5">
                {section.links.filter(l => !l.agencyOnly || user?.role === "admin").map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    style={({ isActive }) => isActive ? {
                      boxShadow: `inset 2px 0 0 0 ${section.color}`,
                    } : undefined}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                        isActive ? "bg-white/10 text-white" : "text-white/65 hover:bg-white/5 hover:text-white"
                      }`
                    }
                    data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {({ isActive }) => (
                      <>
                        <l.icon className="w-4 h-4 shrink-0" style={isActive ? { color: section.color } : undefined} />
                        <span className="flex-1 truncate">{l.label}</span>
                        {l.badgeKey && badges[l.badgeKey] > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] font-bold" style={{ background: section.color }} data-testid={`badge-${l.badgeKey}`}>
                            {badges[l.badgeKey] > 99 ? "99+" : badges[l.badgeKey]}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-white/10 flex-shrink-0 space-y-1">
          {/* Accès Agence — bouton d'accès rapide visible uniquement sur l'instance HUB */}
          {s?.is_hub && user?.role === "admin" && (
            <NavLink
              to="/agency"
              className="mb-2 block w-full p-3 rounded-xl bg-gradient-to-br from-sky-500/20 to-indigo-500/20 border border-sky-500/40 hover:border-sky-400 hover:from-sky-500/30 transition group"
              data-testid="admin-sidebar-open-agency"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center shrink-0">
                  <Rocket className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-sky-400">Espace agence</div>
                  <div className="text-xs font-bold text-white truncate">Ouvrir l'Accès Agence →</div>
                </div>
              </div>
            </NavLink>
          )}
          {/* Onboarding progress (incitatif, disparaît à 100%) */}
          {onboarding?.enabled && !onboarding?.completed_at && (
            <NavLink
              to="/admin/onboarding"
              className="block mb-2 p-3 rounded-xl bg-gradient-to-br from-[color:var(--brand-sky)]/20 to-[color:var(--brand-navy)]/30 border border-[color:var(--brand-sky)]/40 hover:border-[color:var(--brand-sky)]/70 transition group"
              data-testid="sidebar-onboarding-widget"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[color:var(--brand-sky)]">Onboarding</span>
                <span className="text-xs font-black text-white">{onboarding.percent}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[color:var(--brand-sky)] to-emerald-400 transition-all"
                  style={{ width: `${onboarding.percent}%` }}
                />
              </div>
              <div className="text-[10px] text-white/70 mt-1.5 group-hover:text-white">
                Terminer mon profil →
              </div>
            </NavLink>
          )}

          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-white/65 hover:text-white hover:bg-white/5">
            <Globe className="w-4 h-4" /> Voir le site
          </a>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-white/65 hover:text-white hover:bg-white/5" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>

          {/* COCKPIT — versioning + documentation produit (zone agence/template) */}
          <NavLink
            to="/admin/cockpit"
            className={({ isActive }) =>
              `mt-3 w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition text-left ${
                isActive
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-white/[0.03] border-white/10 hover:bg-white/10 hover:border-white/20 text-white/75 hover:text-white"
              }`
            }
            data-testid="admin-sidebar-cockpit"
          >
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[color:var(--brand-sky)] to-[color:var(--brand-navy)] flex items-center justify-center shrink-0">
              <GitCommitHorizontal className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="flex-1 min-w-0 leading-tight">
              <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-white/50">Agence MSC</div>
              <div className="text-xs font-semibold flex items-center gap-1.5">
                COCKPIT
                {(badges?.cockpit ?? 0) > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[9px] font-black px-1 bg-[color:var(--brand-red)] text-white">
                    {badges.cockpit}
                  </span>
                )}
              </div>
            </div>
          </NavLink>

          {/* Studio IA local supprimé — l'IA Design vit uniquement sur l'Accès Agence.
              Les thèmes créés sur l'Accès Agence sont téléchargeables et importables dans Thèmes. */}

          {/* Bloc Agence — provient de l'appairage Accès Agence.
              Masqué sur l'instance Accès Agence elle-même (is_hub=true) : on EST l'agence,
              pas besoin de lien "votre agence" ni de message "injoignable". */}
          {!s?.is_hub && (() => {
            const isPaired = agency?.paired;
            const p = agency?.profile;
            const primary = (p?.team || []).find(m => m.is_primary_contact) || (p?.team || [])[0];
            if (!isPaired) {
              // No agency linked yet
              return (
                <NavLink
                  to="/admin/agency-pairing"
                  className="mt-3 w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 border border-dashed border-white/20 hover:bg-white/10 hover:border-[color:var(--brand-sky)]/60 transition text-left"
                  data-testid="admin-sidebar-agency-unpaired"
                >
                  <div className="w-8 h-8 rounded-full bg-white/5 border border-dashed border-white/20 flex items-center justify-center shrink-0">
                    <Building2 className="w-3.5 h-3.5 text-white/40" />
                  </div>
                  <div className="flex-1 min-w-0 leading-tight">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Aucune agence</div>
                    <div className="text-xs font-semibold text-white/70">Appairer à l'agence →</div>
                  </div>
                </NavLink>
              );
            }
            if (!p) {
              // Paired but agency endpoint unreachable
              return (
                <NavLink
                  to="/admin/agency-pairing"
                  className="mt-3 w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/15 transition text-left"
                  data-testid="admin-sidebar-agency-error"
                >
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                    <Building2 className="w-3.5 h-3.5 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0 leading-tight">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-amber-400/70">Agence injoignable</div>
                    <div className="text-xs font-semibold text-amber-200 truncate">Vérifier l'appairage</div>
                  </div>
                </NavLink>
              );
            }
            return (
              <>
                <button
                  type="button"
                  onClick={() => setAgencyOpen(o => !o)}
                  className="mt-3 w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition group text-left"
                  aria-label={p.name}
                  data-testid="admin-sidebar-agency-btn"
                >
                  {primary?.avatar_url ? (
                    <img src={primary.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                  ) : p.logo_dark_url || p.logo_url ? (
                    <img src={p.logo_dark_url || p.logo_url} alt="" className="w-8 h-8 rounded-full object-contain bg-white/90 p-0.5 shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[color:var(--brand-sky)]/30 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
                      {(primary?.name || p.name || "A")[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0 leading-tight">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Votre agence</div>
                    <div className="text-xs font-semibold text-white truncate">{p.name}</div>
                    {primary && (
                      <div className="text-[10px] text-white/60 truncate">
                        {primary.role} · <span className="text-[color:var(--brand-sky)]">{primary.name}</span>
                      </div>
                    )}
                  </div>
                </button>
                {agencyOpen && (
                  <div className="mt-2 p-3 rounded-xl bg-white/5 border border-white/10 text-[11px] space-y-1.5 animate-in fade-in" data-testid="agency-details-popover">
                    {primary?.role && <div className="text-white/50 uppercase tracking-wider text-[9px] font-bold">{primary.role}</div>}
                    {primary?.name && <div className="text-white font-semibold">{primary.name}</div>}
                    {primary?.email && <a href={`mailto:${primary.email}`} className="block text-white/70 hover:text-[color:var(--brand-sky)] truncate">✉ {primary.email}</a>}
                    {primary?.phone && <a href={`tel:${primary.phone}`} className="block text-white/70 hover:text-[color:var(--brand-sky)]">📞 {primary.phone}</a>}
                    <div className="pt-1.5 border-t border-white/10">
                      {p.email && <a href={`mailto:${p.email}`} className="block text-white/60 hover:text-[color:var(--brand-sky)] truncate">Contact agence · {p.email}</a>}
                      {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="block text-white/60 hover:text-[color:var(--brand-sky)] truncate">{p.website.replace(/^https?:\/\//, "")}</a>}
                      {p.support_message && <div className="text-white/50 italic text-[10px] mt-1">{p.support_message}</div>}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </aside>

      {/* Main content — takes all remaining width */}
      <main className="flex-1 min-w-0 overflow-x-hidden bg-[hsl(var(--background))]">
        {/* Mobile topbar with hamburger */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-3 h-14 bg-[color:var(--brand-navy)] text-white shadow-md" data-testid="admin-mobile-topbar">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="w-10 h-10 rounded-lg hover:bg-white/10 flex items-center justify-center"
            aria-label="Ouvrir le menu"
            data-testid="admin-sidebar-open"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            {s?.logo_url && <img src={s.logo_url} alt="" className="w-7 h-7 rounded-full bg-white p-0.5 object-contain" />}
            <span className="font-display font-bold text-sm truncate">{company}</span>
          </div>
          <InstallPWAButton />
        </div>

        {<ModulesStatusBar />}
        {s?.stripe_mode === "test" && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-1.5 flex items-center justify-center gap-2 text-[11px] font-semibold text-amber-800" data-testid="stripe-test-mode-banner">
            <TestTube2 className="w-3.5 h-3.5" />
            <span>Mode TEST Stripe actif — aucune transaction réelle.</span>
            <Link to="/admin/payments" className="underline hover:text-amber-900">Configurer</Link>
          </div>
        )}
        <Outlet />
      </main>
      <CockpitWhatsNewModal user={user} />
    </div>
  );
}

function ModulesStatusBar() {
  const { settings, refresh } = useSettings();
  const s = settings || {};
  const [modalOpen, setModalOpen] = useState(false);
  const siteStatus = s.site_status || "online";
  const shopStatus = s.shop_status || "online";
  const bookingStatus = s.booking_status || "online";

  // Meta for each status type
  const SITE_META = {
    online:      { label: "En ligne",    color: "emerald" },
    draft:       { label: "Brouillon",   color: "slate" },
    private:     { label: "Privé",       color: "indigo" },
    maintenance: { label: "Maintenance", color: "red" },
  };
  const SHOP_META = {
    online:      { label: "En ligne",            color: "emerald" },
    catalog:     { label: "Catalogue",           color: "sky" },
    draft:       { label: "Brouillon",           color: "slate" },
    vacation:    { label: "Fermeture temporaire", color: "amber" },
    maintenance: { label: "Maintenance",         color: "red" },
  };
  const BOOKING_META = {
    online:   { label: "En ligne",             color: "emerald" },
    draft:    { label: "Brouillon",            color: "slate" },
    vacation: { label: "Fermeture temporaire", color: "amber" },
  };

  const colorDot = (clr) => `bg-${clr}-500`;
  const colorChip = (clr, active) =>
    active
      ? `bg-${clr}-50 text-${clr}-700 border-${clr}-200`
      : `bg-white text-[color:var(--brand-navy)] border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/40`;

  const Pill = ({ testId, label, meta, isOnline }) => (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full border text-[11px] font-semibold ${colorChip(meta.color, true)}`}
      data-testid={testId}
    >
      <span className="relative flex w-2 h-2">
        <span className={`relative inline-flex rounded-full w-2 h-2 ${colorDot(meta.color)}`} />
        {isOnline && <span className={`absolute inset-0 rounded-full ${colorDot(meta.color)} animate-ping opacity-60`} />}
      </span>
      <span className="uppercase tracking-wider text-[9px] font-bold text-[hsl(var(--muted-foreground))]">{label}</span>
      <span className="font-bold">{meta.label}</span>
    </span>
  );

  const siteMeta = SITE_META[siteStatus] || SITE_META.online;
  const shopMeta = SHOP_META[shopStatus] || SHOP_META.online;
  const bookingMeta = BOOKING_META[bookingStatus] || BOOKING_META.online;

  const barTint =
    siteStatus === "maintenance" ? "bg-red-50 border-red-200" :
    siteStatus === "private"     ? "bg-indigo-50 border-indigo-200" :
    siteStatus === "draft"       ? "bg-slate-100 border-slate-200" :
    "bg-[hsl(var(--muted))]/30 border-[hsl(var(--border))]";

  return (
    <>
      <div className={`flex items-center justify-between gap-3 px-6 py-2 border-b ${barTint} flex-wrap`} data-testid="modules-status-bar">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Pill testId="status-pill-site" label="Site" meta={siteMeta} isOnline={siteStatus === "online"} />
          {s.shop_enabled && <Pill testId="status-pill-shop" label="Boutique" meta={shopMeta} isOnline={shopStatus === "online"} />}
          {s.booking_enabled && <Pill testId="status-pill-booking" label="Réservation" meta={bookingMeta} isOnline={bookingStatus === "online"} />}
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="text-[11px] font-bold px-3 py-1.5 rounded-full border bg-white hover:bg-[hsl(var(--muted))]/30 shrink-0"
          data-testid="status-open-modal"
        >
          Gérer les statuts
        </button>
      </div>
      {modalOpen && <SiteStatusModal settings={s} refresh={refresh} onClose={() => setModalOpen(false)} />}
    </>
  );
}

// ------------------------------------------------------------
// Status definitions used across site / shop / booking modules
// ------------------------------------------------------------
const SITE_STATUSES = [
  { v: "online",      l: "En ligne",     d: "Site public, visible par tous.",                        clr: "emerald" },
  { v: "draft",       l: "Brouillon",    d: "Vous voyez le site. Visiteurs = splash 'en préparation'.", clr: "slate" },
  { v: "private",     l: "Privé",        d: "Réservé aux admins. Visiteurs bloqués avec message.",   clr: "indigo" },
  { v: "maintenance", l: "Maintenance",  d: "Travaux — visiteurs voient un écran dédié.",            clr: "red" },
];
const SHOP_STATUSES_LIST = [
  { v: "online",      l: "En ligne",             d: "Produits visibles, commandes actives.",             clr: "emerald" },
  { v: "catalog",     l: "Mode catalogue",       d: "Fiches visibles, commande en ligne désactivée.",    clr: "sky" },
  { v: "vacation",    l: "Fermeture temporaire", d: "Visible mais commandes bloquées (congés).",         clr: "amber" },
  { v: "draft",       l: "Brouillon",            d: "Masqué aux visiteurs. Vous voyez la boutique.",     clr: "slate" },
];
const BOOKING_STATUSES_LIST = [
  { v: "online",   l: "En ligne",             d: "Réservations ouvertes au public.",                clr: "emerald" },
  { v: "vacation", l: "Fermeture temporaire", d: "Visible mais prises de rendez-vous suspendues.",  clr: "amber" },
  { v: "draft",    l: "Brouillon",            d: "Masqué aux visiteurs. Vous voyez l'espace.",       clr: "slate" },
];

function StatusCard({ opt, active, onClick, testId }) {
  return (
    <button
      key={opt.v}
      type="button"
      onClick={onClick}
      className={`p-3 rounded-xl border-2 text-left transition-all ${
        active
          ? `border-${opt.clr}-500 bg-${opt.clr}-50 shadow-md`
          : "border-[hsl(var(--border))] hover:border-[color:var(--brand-navy)]/30"
      }`}
      data-testid={testId}
    >
      <div className={`w-2 h-2 rounded-full bg-${opt.clr}-500 mb-1.5`} />
      <div className="font-bold text-sm text-[color:var(--brand-navy)]">{opt.l}</div>
      <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5 leading-tight">{opt.d}</div>
    </button>
  );
}

function SiteStatusModal({ settings, refresh, onClose }) {
  const [s, setS] = useState({
    site_status: settings.site_status || "online",
    site_maintenance_message: settings.site_maintenance_message || "",
    site_maintenance_image: settings.site_maintenance_image || "",
    site_draft_message: settings.site_draft_message || "",
    site_draft_image: settings.site_draft_image || "",
    shop_status: settings.shop_status || "online",
    shop_status_message: settings.shop_status_message || "",
    shop_status_image: settings.shop_status_image || "",
    shop_vacation_until: settings.shop_vacation_until || "",
    booking_status: settings.booking_status || "online",
    booking_status_message: settings.booking_status_message || "",
    booking_vacation_until: settings.booking_vacation_until || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      refresh();
      onClose();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()} data-testid="site-status-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="font-display font-bold text-xl">Statuts des modules</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Pilotez la visibilité publique du site, de la boutique et des réservations.</p>
          </div>
          <button onClick={onClose} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] text-2xl leading-none">✕</button>
        </div>

        <div className="p-5 space-y-8 max-h-[75vh] overflow-y-auto">

          {/* SITE VITRINE */}
          <section data-testid="site-status-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-[color:var(--brand-navy)]/10 flex items-center justify-center text-[color:var(--brand-navy)]"><Globe className="w-4 h-4" /></div>
              <div>
                <h4 className="font-display font-bold text-[color:var(--brand-navy)]">Site vitrine</h4>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Pages publiques (accueil, services, blog, contact…).</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {SITE_STATUSES.map(opt => (
                <StatusCard
                  key={opt.v}
                  opt={opt}
                  active={s.site_status === opt.v}
                  onClick={() => setS({ ...s, site_status: opt.v })}
                  testId={`site-status-opt-${opt.v}`}
                />
              ))}
            </div>
            {(s.site_status === "draft" || s.site_status === "private") && (
              <div className="mt-4 space-y-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div>
                  <label className="text-xs font-bold text-[color:var(--brand-navy)]">Message visiteur</label>
                  <textarea rows={2} value={s.site_draft_message} onChange={e => setS({ ...s, site_draft_message: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" placeholder={s.site_status === "draft" ? "Notre site arrive bientôt…" : "Ce site est privé, merci de vous connecter."} data-testid="site-draft-message" />
                </div>
                <div>
                  <label className="text-xs font-bold text-[color:var(--brand-navy)]">Image d'illustration (URL)</label>
                  <input value={s.site_draft_image} onChange={e => setS({ ...s, site_draft_image: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" placeholder="https://…/coming-soon.jpg" data-testid="site-draft-image" />
                  {s.site_draft_image && <img src={s.site_draft_image} alt="" className="mt-2 w-48 h-24 object-cover rounded-lg border" />}
                </div>
              </div>
            )}
            {s.site_status === "maintenance" && (
              <div className="mt-4 space-y-3 p-4 rounded-xl bg-red-50 border border-red-200">
                <div>
                  <label className="text-xs font-bold text-red-700">Message visiteur</label>
                  <textarea rows={2} value={s.site_maintenance_message} onChange={e => setS({ ...s, site_maintenance_message: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" placeholder="Site en maintenance — nous revenons très vite !" data-testid="site-maintenance-message" />
                </div>
                <div>
                  <label className="text-xs font-bold text-red-700">Image d'illustration (URL)</label>
                  <input value={s.site_maintenance_image} onChange={e => setS({ ...s, site_maintenance_image: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" placeholder="https://…/maintenance.jpg" />
                  {s.site_maintenance_image && <img src={s.site_maintenance_image} alt="" className="mt-2 w-48 h-24 object-cover rounded-lg border" />}
                </div>
              </div>
            )}
          </section>

          {/* BOUTIQUE */}
          {settings.shop_enabled && (
            <section className="pt-6 border-t" data-testid="shop-status-section">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[color:var(--brand-sky)]/10 flex items-center justify-center text-[color:var(--brand-sky)]"><Store className="w-4 h-4" /></div>
                <div>
                  <h4 className="font-display font-bold text-[color:var(--brand-navy)]">Boutique</h4>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Visibilité des produits et prise de commandes.</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {SHOP_STATUSES_LIST.map(opt => (
                  <StatusCard
                    key={opt.v}
                    opt={opt}
                    active={s.shop_status === opt.v}
                    onClick={() => setS({ ...s, shop_status: opt.v })}
                    testId={`shop-status-opt-${opt.v}`}
                  />
                ))}
              </div>
              {(s.shop_status === "vacation" || s.shop_status === "catalog") && (
                <div className="mt-4 space-y-3 p-4 rounded-xl bg-[hsl(var(--muted))]/30">
                  <div>
                    <label className="text-xs font-bold text-[color:var(--brand-navy)]">Message visiteur</label>
                    <textarea rows={2} value={s.shop_status_message} onChange={e => setS({ ...s, shop_status_message: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" placeholder={s.shop_status === "vacation" ? "Nous sommes en congés du 15 juillet au 20 août. Reprise le 21 août !" : "Mode catalogue — commandez par téléphone au …"} data-testid="shop-status-message" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-[color:var(--brand-navy)]">Image d'illustration (URL)</label>
                    <input value={s.shop_status_image} onChange={e => setS({ ...s, shop_status_image: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" placeholder="https://…/image-conges.jpg" data-testid="shop-status-image" />
                    {s.shop_status_image && <img src={s.shop_status_image} alt="" className="mt-2 w-48 h-24 object-cover rounded-lg border" />}
                  </div>
                  {s.shop_status === "vacation" && (
                    <div>
                      <label className="text-xs font-bold text-[color:var(--brand-navy)]">Date de reprise (facultatif)</label>
                      <input type="date" value={s.shop_vacation_until} onChange={e => setS({ ...s, shop_vacation_until: e.target.value })} className="mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="shop-vacation-until" />
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* RESERVATION */}
          {settings.booking_enabled && (
            <section className="pt-6 border-t" data-testid="booking-status-section">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center text-cyan-700"><Calendar className="w-4 h-4" /></div>
                <div>
                  <h4 className="font-display font-bold text-[color:var(--brand-navy)]">Réservation en ligne</h4>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Prise de rendez-vous publique.</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-2">
                {BOOKING_STATUSES_LIST.map(opt => (
                  <StatusCard
                    key={opt.v}
                    opt={opt}
                    active={s.booking_status === opt.v}
                    onClick={() => setS({ ...s, booking_status: opt.v })}
                    testId={`booking-status-opt-${opt.v}`}
                  />
                ))}
              </div>
              {s.booking_status === "vacation" && (
                <div className="mt-4 space-y-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <div>
                    <label className="text-xs font-bold text-amber-800">Message visiteur</label>
                    <textarea rows={2} value={s.booking_status_message} onChange={e => setS({ ...s, booking_status_message: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" placeholder="Nous sommes en congés — réservations ouvertes à notre retour." data-testid="booking-status-message" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-amber-800">Date de reprise (facultatif)</label>
                    <input type="date" value={s.booking_vacation_until} onChange={e => setS({ ...s, booking_vacation_until: e.target.value })} className="mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="booking-vacation-until" />
                  </div>
                </div>
              )}
            </section>
          )}

        </div>
        <div className="flex justify-end gap-2 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 rounded-full border text-sm">Annuler</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-full bg-[color:var(--brand-navy)] text-white text-sm font-bold disabled:opacity-60" data-testid="site-status-save">
            {saving ? "Enregistrement…" : "Appliquer"}
          </button>
        </div>
      </div>
    </div>
  );
}
